import { CaretRightIcon, ClockIcon, ShieldSlashIcon } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '@voll-intelligence/ui-kit/ui/popover';
import { AirlineMark, hasAirlineMark } from './airline_logos.js';
import { CodeButton } from './code_button.js';
import { cityLabel, splitAirport, voucherStatusPill, type TripStop } from './derive.js';
import { t } from './i18n.js';
import type { ItineraryAddons } from './itinerary_addons.js';
import { stopDetail, stopOpenLabel, stopTitle } from './labels.js';
import { ModalIcon, VoucherStatusBadge } from './primitives.js';
import { PageCardLineView, PageStopState, PageThumb, pageCardView } from './stop_page_view.js';
import { RebookedBadge, RebookingNote, isChangeInProgress } from './rebooked_stop.js';
import { ReturnAirportSwitch } from './return_airport_switch.js';
import { StopGaps } from './stop_gaps.js';
import { StopParty, isPartyStop, stopAmount } from './stop_party.js';
import { modalLabel, money } from './voucher_meta.js';

/** O LOCALIZADOR só no emitido (régua do protótipo): antes da emissão ele não
 *  existe. O código do VOUCHER, não — a Voll o gera na solicitação, e é com ele que
 *  a pessoa cita o serviço na aprovação e no atendimento (#7373); os status mortos
 *  já saíram em `derive.ts`. O LOC é exclusivo do aéreo; a reserva do hotel mora no
 *  documento que a seta abre. */
function stopCodes(stop: TripStop): { locator: string | null; code: string | null } {
  return {
    locator:
      stop.service.status === 'CONFIRMED' && stop.reference?.kind === 'locator'
        ? stop.reference.code
        : null,
    code: isPartyStop(stop) ? null : stop.service.voucherCode,
  };
}

/** A cara do card por modal (a `paradaDoServico` do protótipo): o VOO entra pela
 *  ROTA em IATA com os aeroportos por extenso no detalhe e horários + número no
 *  rodapé; o HOTEL pelo nome com o endereço no detalhe e a janela no rodapé; os
 *  demais modais seguem no vocabulário que já tinham (título + detalhe). */
function stopCardView(stop: TripStop): {
  title: string;
  detail: string | null;
  rodape: string | null;
} {
  const leg = stop.leg;
  if (stop.service.modal === 'FLIGHT' && leg?.from && leg.to) {
    const from = splitAirport(leg.from);
    const to = splitAirport(leg.to);
    // A identidade da cia no rodapé vem CURADA do `stopTitle` (o mesmo juízo de
    // sempre): número quando a logo carrega a marca, nome+número quando não há
    // logo, só o nome quando a fonte não numera — nada disso se recalcula aqui.
    const { flightNo } = stopTitle(stop);
    return {
      title: `${from.code} → ${to.code}`,
      detail:
        cityLabel(leg.from) && cityLabel(leg.to)
          ? `${cityLabel(leg.from)} → ${cityLabel(leg.to)}`
          : null,
      rodape: [`${leg.departTime} → ${leg.arriveTime}${leg.arriveNextDay ? ' +1' : ''}`, flightNo]
        .filter(Boolean)
        .join(' · '),
    };
  }
  const hotel = stop.service.detail;
  if (hotel?.kind === 'HOTEL') {
    const { name } = stopTitle(stop);
    return {
      title: name,
      detail: [hotel.address, hotel.city].filter(Boolean).join(', ') || null,
      // A janela + noites que o detalhe de hoje já compõe ("check-out 20 Jun ·
      // 3 noites") desce pro rodapé — o slot do detalhe é do ENDEREÇO, como no
      // protótipo (a diária não entra: a fonte não manda tarifa por noite, e
      // derivá-la do total mentiria nos impostos).
      rodape: stopDetail(stop),
    };
  }
  const { name, flightNo } = stopTitle(stop);
  return {
    title: [name, flightNo].filter(Boolean).join(' · '),
    detail: stopDetail(stop),
    rodape: null,
  };
}

/** O nome do passo na timeline (#6328): «Voo de ida · 06:20», «Check-in»; os
 *  demais modais pelo nome do modal. A hora sai do card e vem para cá. */
function stepLabel(stop: TripStop): string {
  const modal = stop.service.modal;
  const name =
    modal === 'FLIGHT'
      ? t(stop.leg?.leg === 'volta' ? 'trips.panel.step.return' : 'trips.panel.step.outbound')
      : modal === 'HOTEL'
        ? t('trips.panel.step.checkIn')
        : modalLabel(modal);
  return stop.time ? `${name} · ${stop.time}` : name;
}

/** O thumb do card: a MARCA do serviço — logo da companhia em 28px no voo; os
 *  demais caem no glifo do modal em poço de 42px (o hotel tem foto no protótipo,
 *  mas a fonte não manda fachada no voucher — requisito de dado registrado). */
function StopThumb({ stop }: Readonly<{ stop: TripStop }>) {
  const carrier = stop.leg?.carrier;
  if (stop.service.modal === 'FLIGHT' && hasAirlineMark(carrier)) {
    return <AirlineMark carrier={carrier} className="tp-stop__logo" size={28} />;
  }
  return (
    <span className="tp-stop__thumb" aria-hidden="true">
      <ModalIcon modal={stop.service.modal} size={20} />
    </span>
  );
}

/** As três caras do itinerário: `cards` (o console do operador — a hora dentro do card),
 *  `timeline` (o drawer da conversa, #6328 — o nome do passo sobre o card) e `page` (a
 *  página da viagem, #6313 — a timeline com o card elevado da H6). */
export type ItineraryLayout = 'cards' | 'timeline' | 'page';

export function StopRow({
  stop,
  linked,
  priced,
  onOpenVoucher,
  addons,
  authenticated,
  layout,
}: Readonly<{
  stop: TripStop;
  /** Há outro passo no MESMO dia depois deste — a calha desenha o fio até ele. */
  linked: boolean;
  /** Este passo carrega o VALOR do serviço (o primeiro dele — a volta não repete). */
  priced: boolean;
  onOpenVoucher: (serviceKey: string) => void;
  addons?: ItineraryAddons;
  authenticated: boolean;
  layout: ItineraryLayout;
}>) {
  const { locator, code } = stopCodes(stop);
  // O voo que o back office está trocando sai esmaecido, como no mockup aprovado.
  const muted = isChangeInProgress(stop.service);
  // Em aprovação o card não tem elevação (#6313): a sombra é do serviço emitido.
  const awaiting = stop.service.status === 'AWAITING_APPROVAL';
  const className = [
    'tp-stop',
    linked && 'tp-stop--linked',
    muted && 'tp-stop--muted',
    awaiting && 'tp-stop--awaiting',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={className}>
      <span className="tp-stop__rail" aria-hidden="true">
        {layout === 'page' ? (
          <ModalIcon modal={stop.service.modal} size={16} weight="bold" />
        ) : (
          <ModalIcon modal={stop.service.modal} size={15} />
        )}
      </span>
      {layout !== 'cards' && <span className="tp-stop__step">{stepLabel(stop)}</span>}
      <div className="tp-stop__card">
        {layout === 'page' ? (
          <PageCardBody
            stop={stop}
            priced={priced}
            locator={locator}
            code={code}
            onOpenVoucher={onOpenVoucher}
            addons={addons}
            authenticated={authenticated}
          />
        ) : (
          <ListCardBody
            stop={stop}
            priced={priced}
            locator={locator}
            code={code}
            onOpenVoucher={onOpenVoucher}
            addons={addons}
            authenticated={authenticated}
          />
        )}
        {/* O card INTEIRO é a porta do voucher (spec do drawer, 2026-08-06; a seta
            vira affordance). O ALVO segue sendo este botão — o card tem botão de
            copiar dentro, e botão dentro de botão é HTML inválido — mas o `::after`
            dele se estica sobre o card (`.tp-stop__open::after`), então o clique em
            qualquer ponto chega aqui; os copiáveis se salvam com z-index próprio. */}
        <button
          type="button"
          className="tp-stop__open"
          aria-label={t('trips.panel.openVoucher', { name: stopOpenLabel(stop) })}
          onClick={() => {
            onOpenVoucher(stop.service.key);
          }}
        >
          <CaretRightIcon size={layout === 'page' ? 16 : 14} aria-hidden />
        </button>
      </div>
    </div>
  );
}

interface CardBodyProps {
  stop: TripStop;
  priced: boolean;
  locator: string | null;
  code: string | null;
  onOpenVoucher: (serviceKey: string) => void;
  addons?: ItineraryAddons;
  authenticated: boolean;
}

/** O miolo do card no console e no drawer: quem-e-quando, o texto e o polo. */
function ListCardBody({
  stop,
  priced,
  locator,
  code,
  onOpenVoucher,
  addons,
  authenticated,
}: Readonly<CardBodyProps>) {
  const view = stopCardView(stop);
  return (
    <>
      {/* Quem-e-quando (anatomia do protótipo): o thumb da MARCA no topo e a
          hora ANCORADA no pé — a faixa do meio estica com a altura do card,
          senão a hora "samba" de posição conforme o miolo cresce. */}
      <span className="tp-stop__who">
        <StopThumb stop={stop} />
        <span aria-hidden="true" />
        <span className="tp-stop__time">{stop.time || '—'}</span>
      </span>
      <div className="tp-stop__main">
        <span className="tp-stop__ttl">{view.title}</span>
        {view.detail && <span className="tp-stop__sub">{view.detail}</span>}
        <ReturnAirportSwitch stop={stop} />
        {view.rodape && <span className="tp-stop__sub">{view.rodape}</span>}
        {/* A linha "Antes" fala do primeiro trecho (o do par): só no primeiro passo. */}
        {priced && <RebookingNote service={stop.service} />}
        <StopParty stop={stop} onOpenVoucher={onOpenVoucher} />
        {(locator ?? code) && (
          <span className="tp-stop__codes">
            {locator && <CodeButton kind="locator" code={locator} />}
            {code && <CodeButton kind="voucher" code={code} />}
          </span>
        )}
        <StopGaps stop={stop} addons={addons} authenticated={authenticated} />
      </div>
      {/* O polo do V3 (`PoloDaParada`): o VALOR em cima — com o escudo de
          política colado quando a tarifa estourou o teto — e o selo embaixo. */}
      <StopPole stop={stop} priced={priced} />
    </>
  );
}

/** O miolo do card da página (#6313): o thumb de 44, o título e as linhas da H6, e uma
 *  linha só com os valores do bilhete, as ofertas e os copiáveis (os códigos fecham a
 *  linha). O polo leva o valor e o selo da página. */
function PageCardBody({
  stop,
  priced,
  locator,
  code,
  onOpenVoucher,
  addons,
  authenticated,
}: Readonly<CardBodyProps>) {
  const view = pageCardView(stop);
  return (
    <>
      <PageThumb stop={stop} />
      <div className="tp-stop__main">
        <span className="tp-stop__ttl">{view.title}</span>
        {view.address && <CodeButton kind="address" code={view.address} />}
        {view.lines.map((line) => (
          <PageCardLineView key={line.text} line={line} />
        ))}
        <ReturnAirportSwitch stop={stop} />
        {priced && <RebookingNote service={stop.service} />}
        <StopParty stop={stop} onOpenVoucher={onOpenVoucher} />
        <span className="tp-stop__extras">
          <StopGaps stop={stop} addons={addons} authenticated={authenticated} />
          {locator && <CodeButton kind="locator" code={locator} />}
          {code && <CodeButton kind="voucher" code={code} />}
        </span>
      </div>
      <div className="tp-stop__state">
        {priced && <StopPrice stop={stop} />}
        {priced && <RebookedBadge service={stop.service} />}
        <PageStopState service={stop.service} />
      </div>
    </>
  );
}

/** O valor do serviço, com o escudo de política colado quando a tarifa estourou o teto. */
function StopPrice({ stop }: Readonly<{ stop: TripStop }>) {
  const amount = money(stopAmount(stop), stop.service.currency);
  if (!amount) return null;
  return (
    <span className="tp-stop__price">
      {stop.services.some((s) => s.voucher.within === false) && <PolicyShield />}
      {amount}
    </span>
  );
}

/** O polo do card: o valor (com o escudo de política) e o selo de estado. */
function StopPole({ stop, priced }: Readonly<{ stop: TripStop; priced: boolean }>) {
  return (
    <div className="tp-stop__state">
      {priced && <StopPrice stop={stop} />}
      {priced && <RebookedBadge service={stop.service} />}
      <StopState service={stop.service} />
    </div>
  );
}

/** O escudo de política, colado ao valor do card que estourou o teto — sempre o
 *  ShieldSlash em destructive (regra do design hub). O detalhe mora no popover;
 *  o racional longo é assunto do APROVADOR, não do viajante. */
function PolicyShield() {
  return (
    <Popover>
      <PopoverTrigger
        render={
          <button type="button" className="tp-stop__shield" aria-label={t('trips.policy.out')} />
        }
      >
        <ShieldSlashIcon size={14} aria-hidden />
      </PopoverTrigger>
      <PopoverContent className="genui2 tp-stop__shieldpop" side="top" align="end">
        <p className="tp-stop__shieldttl">{t('trips.policy.out')}</p>
        <p className="tp-stop__shieldsub">{t('trips.policy.needsApproval')}</p>
      </PopoverContent>
    </Popover>
  );
}

/** O estado do serviço no passo: só o CONFIRMADO vira selo — é a promessa
 *  cumprida, e ela pode afirmar. Os outros seguem em texto com o glifo colorido
 *  (regra do protótipo do design): selo de "em emissão" lê como conquista, e
 *  repetido em cada linha do itinerário ele gritava mais que o próprio voo.
 *
 *  Só as esperas chegam aqui: o itinerário filtra os status mortos (a régua da
 *  página), então o ramo vermelho de antes morreu com eles. O emitido com
 *  cancelamento pedido cai no texto âmbar: promessa em suspenso, não cumprida. */
function StopState({ service }: Readonly<{ service: TripStop['service'] }>) {
  const pill = voucherStatusPill(service.status, service.voucher);
  if (!pill) return null;
  if (pill.variant === 'confirmed') {
    return <VoucherStatusBadge variant={pill.variant} labelKey={pill.labelKey} />;
  }
  return (
    <span className="tp-stop__st">
      <ClockIcon size={11} aria-hidden />
      {t(pill.labelKey)}
    </span>
  );
}
